import { useMemo } from "react";
import { labelCopy, formatDuration } from "./sessionReport";

const EMOTION_COLORS = {
  anger: "#ff6d6d",
  angry: "#ff6d6d",
  happy: "#58f09a",
  neutral: "#a9b5c8",
  calm: "#65d8ff",
  sad: "#7d91ff",
  fear: "#ff9b63",
  surprise: "#ffe066",
  disgust: "#d899ff",
  uncertain: "#a9b5c8",
};

export const DETECTION_MODES = [
  {
    id: "fusion",
    label: "Face + voice",
    sub: "Best demo default",
  },
  {
    id: "face",
    label: "Face only",
    sub: "Camera signal",
  },
  {
    id: "voice",
    label: "Voice only",
    sub: "No camera",
  },
];

export function ScoreList({ scores, empty = "Emotion signals appear after you speak." }) {
  const entries = useMemo(
    () => Object.entries(scores || {})
      .map(([label, value]) => [label.toLowerCase(), Number(value) || 0])
      .sort((left, right) => right[1] - left[1])
      .slice(0, 7),
    [scores],
  );

  if (!entries.length) {
    return <p className="voice-agent-empty">{empty}</p>;
  }

  return (
    <div className="voice-agent-scores">
      {entries.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <i>
            <b
              style={{
                width: `${Math.round(value * 100)}%`,
                background: EMOTION_COLORS[label] || "#6ddcff",
              }}
            />
          </i>
          <strong>{Math.round(value * 100)}%</strong>
        </div>
      ))}
    </div>
  );
}

export function SignalCard({
  title,
  label,
  confidence,
  detail,
  active = false,
}) {
  const percent = Math.round((Number(confidence) || 0) * 100);
  return (
    <div className={`voice-agent-signal-card ${active ? "active" : ""}`}>
      <span>{title}</span>
      <strong>{label || "Waiting"}</strong>
      <small>{detail || (percent ? `${percent}% confidence` : "No signal yet")}</small>
    </div>
  );
}

const ARC_COPY = {
  improving: "Ended calmer than it started",
  worsening: "Ended more negative than it started",
  steady: "Stayed steady throughout",
};

export function SessionReportCard({ report, businessName }) {
  if (!report) return null;

  return (
    <section className="voice-agent-session-report" aria-label="Session emotion report">
      <div className="voice-agent-session-report-head">
        <span>Session report{businessName ? ` · ${businessName}` : ""}</span>
        <strong>{labelCopy(report.dominant?.label)} was the dominant signal</strong>
        <small>
          {report.turnCount} analyzed turns · {formatDuration(report.durationMs)} · {ARC_COPY[report.arc] || ARC_COPY.steady}
        </small>
      </div>

      <div className="voice-agent-scores">
        {report.distribution.map((entry) => (
          <div key={entry.label}>
            <span>{labelCopy(entry.label)}</span>
            <i>
              <b
                style={{
                  width: `${entry.pct}%`,
                  background: EMOTION_COLORS[entry.label] || "#6ddcff",
                }}
              />
            </i>
            <strong>{entry.pct}%</strong>
          </div>
        ))}
      </div>

      <div className="voice-agent-session-report-foot">
        <div>
          <span>Opened</span>
          <strong>{labelCopy(report.opening?.label)}</strong>
        </div>
        <div>
          <span>Closed</span>
          <strong>{labelCopy(report.closing?.label)}</strong>
        </div>
        <div>
          <span>Adaptations</span>
          <strong>{report.adaptationCount}</strong>
        </div>
      </div>

      <p className="voice-agent-session-report-note">
        This is what a business would get from Session Insights after any support call, user
        interview, or usability test — generated automatically from the same detect → update →
        adapt loop you just watched.
      </p>
    </section>
  );
}

export function Timeline({ items }) {
  if (!items.length) {
    return <p className="voice-agent-empty">Loop events will appear here.</p>;
  }
  return (
    <ol className="voice-agent-timeline">
      {items.slice(0, 6).map((item) => (
        <li key={`${item.at}-${item.message}`}>
          <span>{new Date(item.at).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}</span>
          <p>{item.message}</p>
        </li>
      ))}
    </ol>
  );
}
